import React from 'react';
import { TouchableOpacity } from 'react-native';
import {
  bool,
  node,
  string,
  object,
} from 'prop-types';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import ssPropTypes from '../../../modules/ssPropTypes';
import {
  BodyText,
  Button,
  Icon,
} from '..';
import { SymbolsKlarnaColorIcon } from '../Icons/SymbolsKlarnaColorIcon';
import discoverIcon from '../../../../images/cc_discover_minimal.png';
import visaIcon from '../../../../images/cc_visa_minimal.png';
import mastercardIcon from '../../../../images/cc_mastercard_minimal.png';
import amexIcon from '../../../../images/cc_amex_minimal.png';
import jcbIcon from '../../../../images/cc_jcb_minimal.png';
import defaultIcon from '../../../../images/cc_cvv_minimal.png';
import { openKlarnaBusinessUrl } from '../../../modules/klarna';

/**
 * These banks have their own card icons and sometimes short names defined
 * (Visa, Discover, Mastercard, American Express, and JCB)
 */
const CARD_CONFIG = {
  visa: {
    shortName: 'VISA',
    icon: visaIcon,
  },
  mastercard: {
    shortName: 'MC',
    icon: mastercardIcon,
  },
  discover: {
    shortName: 'DISC',
    icon: discoverIcon,
  },
  'american-express': {
    shortName: 'AMEX',
    icon: amexIcon,
  },
  jcb: {
    icon: jcbIcon,
  },
  klarna: {
    icon: <SymbolsKlarnaColorIcon height={20} />,
  },
};

/**
 * Display a credit card number and it's icon.
 *
 * @param {Object} card - The credit card object returned from the backend.
 * @param {Boolean} cardError - Boolean indicating if card has been declined or failed pre-auth
 */
const propTypes = {
  /** The credit card instrument. */
  card: ssPropTypes.creditCard.isRequired,
  /** card error state */
  cardError: bool,
  /** Hide the card icon */
  hideLogo: bool,
  /** Hide the short name for the bank */
  hideBankShortName: bool,
  /** elements within the component to render */
  children: node,
  /** classname for the children elements */
  className: string,
  /** Style object to passed to the root element of this component */
  style: object,
  /** Which character to use for redaction */
  redactChar: string,
  /** lastFourPrefix */
  last4Prefix: string,
  /** Text color */
  color: string,
};

const defaultProps = {
  cardError: false,
  hideLogo: false,
  className: '',
  children: null,
  hideBankShortName: false,
  style: null,
  redactChar: '*',
  last4Prefix: null,
  color: undefined,
};

export default function CreditCard({
  card,
  cardError,
  hideBankShortName,
  hideLogo,
  children,
  className,
  style,
  redactChar,
  last4Prefix,
  color,
}) {
  const bankNormalized = card.bank ? card.bank.toLowerCase().replace(/\s+/, '-') : '';
  const { icon, shortName: bankShortName = '' } = CARD_CONFIG[bankNormalized] || {
    icon: defaultIcon,
  };
  const handleKlarnaUrlClicked = event => {
    event.stopPropagation();
    openKlarnaBusinessUrl();
  };

  const textColor = cardError ? BrandColor.red : color;

  return (
    <Inline
      alignItems="center"
      space={8}
      className={className}
      style={style}
      testID={`credit-card-container-${bankNormalized}`}
    >
      {!hideLogo && (
        typeof icon === 'string'
          ? (
            <Icon
              uri={icon}
              height={20}
              width={30}
              accessibilityLabel={`${bankNormalized} Logo`}
              testID={`cc-logo-icon-wrapper-${bankNormalized}`}
            />
          )
          : <Box testID={`cc-logo-icon-wrapper-${bankNormalized}`}>{icon}</Box>
      )}
      <Inline alignItems="center" justifyContent="space-between" flex={1}>
        <BodyText color={textColor}>
          {!hideBankShortName && `${bankShortName} `}
          {card.last4 && !last4Prefix && (
            <BodyText color={textColor} testID="credit-card-last4">
              {redactChar}&thinsp;{redactChar}&thinsp;{redactChar}&thinsp;{redactChar} {card.last4}
            </BodyText>
          )}
          {card.last4 && last4Prefix && (
            <BodyText color={textColor} testID="credit-card-last4">
              {last4Prefix} {card.last4}
            </BodyText>
          )}
          {bankNormalized === 'klarna' && (
            // TouchableOpacity is necessary when CreditCard is nested within a Touchable
            // to ensure our handler executes first. Button onClick events are fired after
            // onPress and could get cancelled
            <TouchableOpacity onPress={handleKlarnaUrlClicked} testID="credit-card-link">
              <Button
                appearance="link-lowercase"
                size="medium"
                clickTracking="klarna_creditcard_clicked"
                stopPropagation
              >
                Learn More
              </Button>
            </TouchableOpacity>
          )}
        </BodyText>
        {children}
      </Inline>
    </Inline>
  );
}
CreditCard.propTypes = propTypes;
CreditCard.defaultProps = defaultProps;
